import {useCallback, useMemo} from 'react';
import assert from 'assert-ts';
import type {ThesaurusId} from 'schemaDefinition/types';
import type {ThesaurusNode} from 'services/thesaurus/types';
import {getNodeDisplayCode} from 'services/thesaurus';
import {useMultiThesaurusEditStateContext} from '../contexts';

export const useThesaurusContext = (thesaurusId: ThesaurusId) => {
  const {
    showCodes,
    thesauruses,
    searchQuery,
    searchResult,
    getNodeAction,
    addNodeId,
    removeNodeId,
  } = useMultiThesaurusEditStateContext();

  const getDisplayCode = useCallback(
    (node: ThesaurusNode) => {
      const showCode = showCodes[thesaurusId]!;
      return getNodeDisplayCode(node, showCode, undefined);
    },
    [showCodes, thesaurusId],
  );

  return useMemo(
    () => ({
      thesaurus: assert(
        thesauruses[thesaurusId],
        'useThesaurusResult: Thesaurus not found',
        {thesaurusId},
      ),
      searchQuery,
      searchResult: searchResult?.[thesaurusId],
      getDisplayCode,
      getNodeAction: (nodeId: string) => getNodeAction(thesaurusId, nodeId),
      addNodeId: (nodeId: string) => addNodeId(thesaurusId, nodeId),
      removeNodeId: (nodeId: string) => removeNodeId(thesaurusId, nodeId),
    }),
    [
      addNodeId,
      getDisplayCode,
      getNodeAction,
      removeNodeId,
      searchQuery,
      searchResult,
      thesaurusId,
      thesauruses,
    ],
  );
};
